import { BarDatum } from '@nivo/bar';
import { BarItem } from '@nivo/bar';
import React, { useCallback, useMemo, useState } from 'react';
import { addAlpha } from 'libs/end-to-end/src/progress-page-utils';
import { BarItemProps } from '@nivo/bar/dist/types/types';
import { NoopTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';
import { OutlineTooltip } from 'apps/sigma2-ui/src/components/ui/Tooltip';
import { animated } from '@react-spring/web';
import { to } from '@react-spring/web';
import { useSpringValue } from '@react-spring/web';
import { v4 as uuidv4 } from 'uuid';

export const S2ChartBar = ({
  barProps,
  onMouseEnter,
  onMouseLeave,
  tooltip,
  shouldDisplayVertically,
  hoverEffect,
}: {
  barProps: BarItemProps<BarDatum>;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  tooltip?: React.ReactNode;
  shouldDisplayVertically: boolean;
  hoverEffect: boolean;
  onClick?: (barProps: BarItemProps<BarDatum>) => void;
}) => {
  const [open, setOpen] = useState(false);

  const { value } = barProps.bar.data;

  // Place the bottom of the tooltip 30px bellow the bar height
  const barHeight = barProps.bar.height;
  const tooltipYPosition = barHeight - 30;

  const minBarHeight = barHeight < 2 ? 2 : barHeight;

  const heightSpring = useSpringValue(minBarHeight);
  const transformSpring = useSpringValue('');

  const newBarProps = useMemo(
    () => ({
      ...barProps,
      bar: {
        ...barProps.bar,
        y: barHeight === 0 ? -100 : barProps.bar.y,
      },
    }),
    [barHeight, barProps],
  );

  const handleOnHover = useCallback(() => {
    setOpen(true);
    onMouseEnter();
  }, [onMouseEnter]);

  const handleOnHoverLeave = useCallback(() => {
    setOpen(false);
    onMouseLeave();
  }, [onMouseLeave]);

  const barStyle = useMemo(() => {
    const colorWithAlpha = addAlpha(barProps.bar.color, 0.5);
    return open && hoverEffect
      ? {
          filter: `drop-shadow(0px 0px 3px ${colorWithAlpha})`,
        }
      : {};
  }, [barProps.bar.color, open, hoverEffect]);

  const clipWidth = useCallback(
    (value: number) =>
      Math.max(
        value + (shouldDisplayVertically ? barProps.borderRadius : 0),
        0,
      ),
    [barProps.borderRadius, shouldDisplayVertically],
  );

  const clipHeight = useCallback(
    (value: number) =>
      Math.max(
        value + (shouldDisplayVertically ? 0 : barProps.borderRadius),
        0,
      ),
    [barProps.borderRadius, shouldDisplayVertically],
  );

  const barItemStyle = useMemo(
    () => ({
      ...barProps.style,
      transform: transformSpring,
      height: heightSpring,
    }),
    [barProps.style, heightSpring, transformSpring],
  );

  const popperProps = useMemo(
    () => ({
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [-tooltipYPosition, -10],
          },
        },
      ],
    }),
    [tooltipYPosition],
  );

  const uuid = uuidv4();

  const transformTransparentX = barProps.bar.x;
  const transformTransparentY =
    barHeight < 2 ? barProps.bar.y - 50 : barProps.bar.y;
  const transparentBarHeight = barHeight < 2 ? 50 : barProps.bar.height;

  // extra Y axis translate so small bars (bellow 2px height) are visible on
  // top of the 0% line
  const extraYTranslate = value >= 0 && value < 2 && minBarHeight <= 2 ? 2 : 0;
  const transform = `translate(${barProps.bar.x}, ${barProps.bar.y - extraYTranslate})`;

  const bar = useMemo(() => {
    return (
      <g
        style={barStyle}
        data-testid={
          barProps.ariaLabel && barProps.ariaLabel(barProps.bar.data)
        }
      >
        <g
          transform={`translate(${transformTransparentX}, ${transformTransparentY})`}
        >
          <rect
            width={barProps.style.width.get()}
            height={`${transparentBarHeight}px`}
            fill="transparent"
          />
        </g>

        <animated.g transform={transform}>
          <defs>
            <clipPath id={uuid}>
              <animated.rect
                x={
                  !shouldDisplayVertically
                    ? 0
                    : value >= 0
                      ? -barProps.borderRadius
                      : 0
                }
                y={value >= 0 ? 0 : -barProps.borderRadius}
                rx={barProps.borderRadius}
                ry={barProps.borderRadius}
                width={to(barProps.style.width, clipWidth)}
                height={to(minBarHeight, clipHeight)}
              />
            </clipPath>
          </defs>

          <animated.g clipPath={`url(#${uuid})`}>
            <BarItem {...newBarProps} style={barItemStyle} borderRadius={0} />
          </animated.g>
        </animated.g>
      </g>
    );
  }, [
    barItemStyle,
    barProps,
    barStyle,
    clipHeight,
    clipWidth,
    minBarHeight,
    newBarProps,
    shouldDisplayVertically,
    transform,
    transformTransparentX,
    transformTransparentY,
    transparentBarHeight,
    uuid,
    value,
  ]);

  return tooltip ? (
    <OutlineTooltip
      title={tooltip}
      placement="right-end"
      onOpen={handleOnHover}
      onClose={handleOnHoverLeave}
      PopperProps={popperProps}
      width="fit-content"
    >
      {bar}
    </OutlineTooltip>
  ) : (
    <NoopTooltip
      title={<></>}
      placement="right-end"
      onOpen={handleOnHover}
      onClose={handleOnHoverLeave}
      PopperProps={popperProps}
    >
      {bar}
    </NoopTooltip>
  );
};
